import React, { useState, useEffect } from 'react';
import { Settings, CheckCircle, AlertCircle, RefreshCw, Trash2, Plus, Shield } from 'lucide-react';
import { whatsappService } from 'api/services/whatsapp.service';
import { WhatsAppEmbeddedSignupConfig, CreateEmbeddedSignupConfigRequest, UpdateEmbeddedSignupConfigRequest } from 'types';
import { useAuth } from 'hooks/useAuth';
import { useFeatureGrants } from 'hooks/useFeatureGrants';

const EmbeddedSignupConfig: React.FC = () => {
  const { currentOrganization } = useAuth();
  const [config, setConfig] = useState<WhatsAppEmbeddedSignupConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    facebookAppId: '',
    facebookAppSecret: '',
    configurationId: '',
    solutionId: '',
    redirectUri: '',
    businessManagerId: '',
    displayName: '',
    description: '',
    active: true,
  });

  // Who manages this config (user decision 14 Oct 2026): master/platform admins,
  // or a provider admin of THIS org once master has granted the org
  // WHATSAPP_EMBEDDED_SIGNUP_CONFIG (Service Controls -> Feature Access). Everyone
  // else inherits the platform's config through WhatsApp Setup and never needs
  // this page. The server enforces the same rule; this only avoids a page of 403s.
  const grants = useFeatureGrants();
  const role = (currentOrganization?.role || '').toLowerCase();
  const isPlatformAdmin = role === 'master_admin' || role === 'platform_admin';
  const isProviderAdmin = ['super_admin', 'service_admin', 'provider_owner', 'provider_admin'].includes(role);
  const grantsLoading = !isPlatformAdmin && isProviderAdmin && grants === undefined;
  const isAdmin = isPlatformAdmin ||
                  (isProviderAdmin && !!grants && grants.includes('WHATSAPP_EMBEDDED_SIGNUP_CONFIG'));

  useEffect(() => {
    if (isAdmin) {
      fetchConfig();
    }
  }, [isAdmin]);

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await whatsappService.getOwnEmbeddedSignupConfig();

      if (response.success && response.data) {
        setConfig(response.data);
        // Pre-fill form with existing config (except facebookAppSecret - never returned)
        setFormData({
          facebookAppId: response.data.facebookAppId || '',
          facebookAppSecret: '', // Never pre-fill - it's a secret
          configurationId: response.data.configurationId || '',
          solutionId: response.data.solutionId || '',
          redirectUri: response.data.redirectUri || '',
          businessManagerId: response.data.businessManagerId || '',
          displayName: response.data.displayName || '',
          description: response.data.description || '',
          active: response.data.active ?? true,
        });
      } else {
        // No config exists yet
        setConfig(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load embedded signup configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConfig = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentOrganization?.id) {
      alert('❌ Organization ID not found');
      return;
    }

    setSaving(true);

    const request: CreateEmbeddedSignupConfigRequest = {
      organizationId: currentOrganization.id,
      facebookAppId: formData.facebookAppId,
      facebookAppSecret: formData.facebookAppSecret,
      configurationId: formData.configurationId || undefined,
      solutionId: formData.solutionId || undefined,
      redirectUri: formData.redirectUri,
      businessManagerId: formData.businessManagerId || undefined,
      displayName: formData.displayName || undefined,
      description: formData.description || undefined,
    };

    const result = await whatsappService.createEmbeddedSignupConfig(request);

    if (result.success) {
      alert('✅ Embedded signup configuration created successfully!');
      fetchConfig();
    } else {
      alert(`❌ ${result.error || 'Failed to create configuration'}`);
    }

    setSaving(false);
  };

  const handleUpdateConfig = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!config?.id) {
      alert('❌ Configuration ID not found');
      return;
    }

    setSaving(true);

    const request: UpdateEmbeddedSignupConfigRequest = {
      facebookAppSecret: formData.facebookAppSecret || undefined, // Only update if provided
      configurationId: formData.configurationId || undefined,
      solutionId: formData.solutionId || undefined,
      redirectUri: formData.redirectUri || undefined,
      businessManagerId: formData.businessManagerId || undefined,
      displayName: formData.displayName || undefined,
      description: formData.description || undefined,
      active: formData.active,
    };

    const result = await whatsappService.updateEmbeddedSignupConfig(config.id, request);

    if (result.success) {
      alert('✅ Embedded signup configuration updated successfully!');
      fetchConfig();
    } else {
      alert(`❌ ${result.error || 'Failed to update configuration'}`);
    }

    setSaving(false);
  };

  const handleDeleteConfig = async () => {
    if (!config?.id) return;

    if (!confirm('⚠️ Are you sure you want to delete this embedded signup configuration? This will affect all child organizations that inherit from it.')) {
      return;
    }

    setDeleting(true);

    const result = await whatsappService.deleteEmbeddedSignupConfig(config.id);

    if (result.success) {
      alert('✅ Embedded signup configuration deleted successfully!');
      setConfig(null);
      setFormData({
        facebookAppId: '',
        facebookAppSecret: '',
        configurationId: '',
        solutionId: '',
        redirectUri: '',
        businessManagerId: '',
        displayName: '',
        description: '',
        active: true,
      });
    } else {
      alert(`❌ ${result.error || 'Failed to delete configuration'}`);
    }

    setDeleting(false);
  };

  if (grantsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Access control check
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 max-w-md">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-red-600 dark:text-red-400" />
            <div>
              <h3 className="font-semibold text-red-900 dark:text-red-300">Access Denied</h3>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                Embedded signup configuration is managed by the platform. Your organization inherits the
                platform&apos;s configuration, so your businesses can onboard WhatsApp without one of their own.
                Contact the platform team if you need your own Meta app.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading configuration...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
          <div>
            <h3 className="font-semibold text-red-900 dark:text-red-300">Error Loading Configuration</h3>
            <p className="text-sm text-red-700 dark:text-red-400 mt-1">{error}</p>
          </div>
        </div>
        <button
          onClick={fetchConfig}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  const isConfigured = !!config;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Settings className="w-7 h-7" />
            Embedded Signup Configuration
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Configure Facebook embedded signup for WhatsApp Business API
          </p>
        </div>
        {isConfigured && (
          <div className="flex items-center gap-2 px-4 py-2 bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400 rounded-lg">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Configured</span>
          </div>
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">ℹ️ About Embedded Signup</h3>
        <p className="text-sm text-blue-800 dark:text-blue-400 mb-2">
          Embedded signup allows businesses to connect their WhatsApp Business Account through Facebook Login,
          without manually copying credentials. This configuration will be inherited by all child organizations.
        </p>
        <p className="text-sm text-blue-800 dark:text-blue-400">
          <strong>Organization:</strong> {currentOrganization?.name || 'Unknown'}
        </p>
      </div>

      {/* Configuration Status */}
      {isConfigured && config && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Current Configuration</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Facebook App ID</p>
              <p className="font-medium text-gray-900 dark:text-white">{config.facebookAppId}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Configuration ID</p>
              <p className="font-medium text-gray-900 dark:text-white">{config.configurationId}</p>
            </div>
            {config.solutionId && (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Partner Solution ID</p>
                <p className="font-medium text-gray-900 dark:text-white">{config.solutionId}</p>
              </div>
            )}
            {config.redirectUri && (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Redirect URI</p>
                <p className="font-medium text-gray-900 dark:text-white truncate">{config.redirectUri}</p>
              </div>
            )}
            {config.businessManagerId && (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Business Manager ID</p>
                <p className="font-medium text-gray-900 dark:text-white">{config.businessManagerId}</p>
              </div>
            )}
            {config.displayName && (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Display Name</p>
                <p className="font-medium text-gray-900 dark:text-white">{config.displayName}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
              <span
                className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                  config.active
                    ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                }`}
              >
                {config.active ? 'Active' : 'Inactive'}
              </span>
            </div>
            {config.createdAt && (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Created At</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {new Date(config.createdAt).toLocaleString()}
                </p>
              </div>
            )}
            {config.updatedAt && (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Updated At</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {new Date(config.updatedAt).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Configuration Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
          {isConfigured ? 'Update Configuration' : 'Create Configuration'}
        </h2>

        <form onSubmit={isConfigured ? handleUpdateConfig : handleCreateConfig} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Facebook App ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.facebookAppId}
              onChange={(e) => setFormData({ ...formData, facebookAppId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter Facebook App ID"
              required
              disabled={isConfigured}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              The App ID from your Facebook Developer App{isConfigured && ' (cannot be changed)'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Facebook App Secret {!isConfigured && <span className="text-red-500">*</span>}
            </label>
            <input
              type="password"
              value={formData.facebookAppSecret}
              onChange={(e) => setFormData({ ...formData, facebookAppSecret: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={isConfigured ? 'Leave blank to keep current' : 'Enter Facebook App Secret'}
              required={!isConfigured}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              The App Secret from your Facebook Developer App
              {isConfigured && ' (leave blank to keep current - never displayed for security)'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Configuration ID (Optional)
            </label>
            <input
              type="text"
              value={formData.configurationId}
              onChange={(e) => setFormData({ ...formData, configurationId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter Configuration ID"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              The embedded signup configuration ID from Meta Business Manager
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Partner Solution ID (Optional)
            </label>
            <input
              type="text"
              value={formData.solutionId}
              onChange={(e) => setFormData({ ...formData, solutionId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter Partner Solution ID"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              The solution ID for partner integrations (provided by Meta for Tech Providers)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Redirect URI {!isConfigured && <span className="text-red-500">*</span>}
            </label>
            <input
              type="url"
              value={formData.redirectUri}
              onChange={(e) => setFormData({ ...formData, redirectUri: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="https://your-domain.com/whatsapp/callback"
              required={!isConfigured}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              The callback URL after successful signup
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Business Manager ID (Optional)
            </label>
            <input
              type="text"
              value={formData.businessManagerId}
              onChange={(e) => setFormData({ ...formData, businessManagerId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter Business Manager ID"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Display Name (Optional)
            </label>
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Production WhatsApp Setup"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description (Optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter a description for this configuration"
              rows={3}
            />
          </div>

          {/* Active checkbox only for updates */}
          {isConfigured && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="active" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Active (enabled for use)
              </label>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="tl-btn tl-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {isConfigured ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  {isConfigured ? <CheckCircle className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  {isConfigured ? 'Update Configuration' : 'Create Configuration'}
                </>
              )}
            </button>

            {isConfigured && (
              <button
                type="button"
                onClick={handleDeleteConfig}
                disabled={deleting}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete Configuration
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Help Section */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
        <h3 className="font-semibold text-yellow-900 dark:text-yellow-300 mb-2">📖 How to Get Your Configuration</h3>
        <ol className="text-sm text-yellow-800 dark:text-yellow-400 space-y-2 list-decimal list-inside">
          <li>Go to <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="underline">Meta Developers</a></li>
          <li>Create or select your Facebook App</li>
          <li>Copy your <strong>App ID</strong> and <strong>App Secret</strong> from Settings → Basic</li>
          <li>Add "WhatsApp" product to your app</li>
          <li>Navigate to WhatsApp → Configuration</li>
          <li>Set up Embedded Signup and copy the <strong>Configuration ID</strong> (optional)</li>
          <li>Configure the <strong>Redirect URI</strong> to: <code className="dark:text-yellow-300">{window.location.origin}/whatsapp/setup</code></li>
          <li>Save the configuration here - it will be inherited by all child organizations</li>
        </ol>
        <div className="mt-4 p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded border border-yellow-300 dark:border-yellow-700">
          <p className="text-xs text-yellow-900 dark:text-yellow-300">
            <strong>Security Note:</strong> The App Secret is stored securely and never displayed after saving.
            It's used to verify Facebook API requests.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmbeddedSignupConfig;
